import { useEffect, useRef, useState } from 'react';
import { apiFetch, descargarArchivo } from '../../lib/api';
import { Ayuda } from '../../components/Ayuda';
import { progresoEstudio, EstadoEstudio } from './EstadoEstudio';
import { Spinner } from '../../components/Spinner';
import { colaRuta, type Favorito } from './tipos';

export const AYUDA_ESTUDIO = 'Cuánto de la carpeta ya leyó Andrés: la plataforma descarga y extrae el texto de cada documento para que los agentes puedan citarlo. Mientras no esté "listo", Andrés responde con lo que ya leyó y puede faltarle un anexo. Se actualiza solo cada pocos segundos mientras hay un estudio en curso. "listo · 57 %" significa que el resto son archivos sin texto extraíble (imágenes y audios, que se transcriben en la Fase 2; o archivos enormes, catalogados solo por nombre).';
export const AYUDA_FASE2 = 'Fase 2 (opcional): Iris y Eco transcriben imágenes y audios de la carpeta. Tarda y tiene costo; lánzala solo si esos archivos importan para la DIA.';

/** Barra de progreso del estudio con su porcentaje (chip del listado). */
function Barra({ f }: { f: Favorito }) {
  const p = progresoEstudio(f);
  return (
    <Ayuda texto={AYUDA_ESTUDIO}>
      <span className="inline-flex items-center gap-2 text-[11.5px] font-normal text-gris">
        <span>Estudio</span>
        <span aria-hidden="true" className="h-1.5 w-20 overflow-hidden rounded-full bg-slate-100">
          <span
            className={`block h-full rounded-full ${p.activo ? 'animate-pulse bg-azul-profundo' : p.pct === 100 ? 'bg-emerald-500' : 'bg-azul-medio'}`}
            style={{ width: `${p.pct ?? 0}%` }}
          />
        </span>
        {p.activo ? (
          <span className="inline-flex items-center gap-1.5 text-azul-profundo">
            <Spinner tamano="xs" />
            <EstadoEstudio f={f} />
          </span>
        ) : (
          <span className={`tabular-nums ${p.pct === 100 ? 'text-emerald-600' : ''}`}>{p.etiqueta}</span>
        )}
      </span>
    </Ayuda>
  );
}

/** Fila del listado de DIAs en trabajo (2026-10-22): nombre, carpeta, chips de estado, un botón
 *  primario (Abrir) y el resto de acciones en el menú ⋯. En móvil se apila. */
export function FilaDia({ f, onAbrir, onAnalisis, onCambio, onError }: { f: Favorito; onAbrir: () => void; onAnalisis: () => void; onCambio: () => Promise<void>; onError: (m: string) => void }) {
  const [menu, setMenu] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menu) return;
    const cerrar = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setMenu(false); };
    document.addEventListener('mousedown', cerrar);
    return () => document.removeEventListener('mousedown', cerrar);
  }, [menu]);
  const ocupado = ['ingestando', 'analizando', 'enriqueciendo'].includes(f.sync_estado ?? '');
  const listo = (f.docs_listos ?? 0) > 0;
  const accion = async (path: string, method: 'POST' | 'DELETE') => {
    setMenu(false);
    try { await apiFetch(path, { method }); await onCambio(); } catch (e) { onError(e instanceof Error ? e.message : 'La acción falló.'); }
  };
  const multimedia = f.sync_estado === 'listo' && ((f.analisis?.imagenes ?? 0) + (f.analisis?.audios ?? 0)) > 0;
  return (
    <li className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:gap-4">
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] font-medium text-azul-noche"><span aria-hidden="true" className="mr-1 text-amber-400">★</span>{f.nombre}</p>
        <p className="truncate text-[11px] font-light text-gris" title={f.ruta}>SharePoint · {colaRuta(f.ruta)}</p>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
          <Barra f={f} />
          {f.ficha ? (
            <Ayuda texto={`Campos de la Ficha DIA con valor: ${f.ficha.con} de ${f.ficha.total}. Cada campo vacío termina como [FALTA] en algún capítulo. La ficha es la fuente de verdad del proyecto y se corrige en su Excel.${f.ficha.problemas ? ` Hay ${f.ficha.problemas} aviso(s) de validación: revísalos en la pestaña Ficha.` : ''}`}>
              <span className={`chip ${f.ficha.problemas ? 'bg-amber-50 text-amber-800 ring-amber-200' : 'bg-celeste/20 text-azul-profundo ring-celeste/60'}`}>Ficha {f.ficha.con}/{f.ficha.total}{f.ficha.problemas ? ' ⚠' : ''}</span>
            </Ayuda>
          ) : (
            <Ayuda texto="Este proyecto no tiene Ficha DIA. Descarga la plantilla (⋯ → Descargar plantilla de ficha), llénala y déjala en la carpeta del proyecto como 'Ficha DIA - <proyecto>.xlsx'; se importa al sincronizar.">
              <span className="chip bg-slate-100 text-gris ring-slate-200">Sin ficha</span>
            </Ayuda>
          )}
          {(f.capitulos_desactualizados ?? 0) > 0 && (
            <Ayuda texto="La ficha cambió después de generar estos capítulos. En la pestaña Capítulos ves qué cambió y puedes actualizarlos con un clic.">
              <span className="chip bg-red-50 text-red-700 ring-red-200">⚠ {f.capitulos_desactualizados} capítulo{f.capitulos_desactualizados === 1 ? '' : 's'} desactualizado{f.capitulos_desactualizados === 1 ? '' : 's'}</span>
            </Ayuda>
          )}
          {f.sync_estado === 'analizado' && (
            <button type="button" onClick={onAnalisis} className="text-[11.5px] font-medium text-amber-700 hover:underline">📋 Ver análisis y estudiar</button>
          )}
          {f.sync_estado === 'error' && (
            <Ayuda texto="La última sincronización se interrumpió. Usa ⋯ → Sincronizar para reintentar; retoma desde donde iba.">
              <span className="chip bg-red-50 text-red-700 ring-red-200">interrumpido</span>
            </Ayuda>
          )}
          {f.sync_estado === 'enriqueciendo' && (
            <Ayuda texto={AYUDA_FASE2}><span className="chip bg-celeste/20 text-azul-profundo ring-celeste/60">Fase 2 en curso</span></Ayuda>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Ayuda texto={listo ? 'Abre el proyecto: chat con Andrés y sus capítulos.' : 'Aún no hay documentos estudiados: prepara la carpeta primero (⋯ → Preparar).'} toque={false} className="flex flex-1 sm:flex-none">
          <button type="button" onClick={onAbrir} disabled={!listo} className="btn-primario w-full sm:w-auto disabled:opacity-50">Abrir</button>
        </Ayuda>
        <div ref={ref} className="relative">
          <button type="button" onClick={() => setMenu((v) => !v)} aria-haspopup="menu" aria-expanded={menu} aria-label={`Más acciones para ${f.nombre}`} className="flex h-9 w-9 items-center justify-center rounded-lg text-lg text-gris hover:bg-slate-100 hover:text-azul-noche">⋯</button>
          {menu && (
            <div role="menu" className="absolute right-0 z-20 mt-1 w-60 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
              <button role="menuitem" type="button" disabled={ocupado} onClick={() => void accion(`/api/sharepoint/favoritos/${f.id}/ingestar`, 'POST')} className="block w-full px-3 py-2 text-left text-[13px] text-texto hover:bg-slate-50 disabled:opacity-50">
                {f.sync_estado === 'listo' ? '🔄 Sincronizar con SharePoint' : '📥 Preparar (estudiar la carpeta)'}
              </button>
              {multimedia && (
                <button role="menuitem" type="button" onClick={() => void accion(`/api/sharepoint/favoritos/${f.id}/enriquecer`, 'POST')} className="block w-full px-3 py-2 text-left text-[13px] text-texto hover:bg-slate-50" title={AYUDA_FASE2}>
                  🖼 Fase 2 (imágenes y audios)
                </button>
              )}
              <button role="menuitem" type="button" onClick={() => { setMenu(false); void descargarArchivo(`/api/dia/ficha/plantilla?nombre=${encodeURIComponent(f.nombre)}`, `Ficha DIA - ${f.nombre}.xlsx`); }} className="block w-full px-3 py-2 text-left text-[13px] text-texto hover:bg-slate-50" title="Excel vacío de la Ficha DIA: llénalo y déjalo en la carpeta del proyecto">
                📋 Descargar plantilla de ficha
              </button>
              <button role="menuitem" type="button" onClick={() => void accion(`/api/sharepoint/favoritos/${f.id}`, 'DELETE')} className="block w-full px-3 py-2 text-left text-[13px] text-red-600 hover:bg-red-50">
                Quitar del listado
              </button>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}
